import Reveal from "@/components/Reveal";

const AUDIENCES = [
  {
    num: "01",
    name: "Photonic computing companies",
    body: "Your architecture is still on the bench. The simulator lets you pressure-test modulator choices, non-ideality budgets, and array sizing in software — before a single chip is fabbed.",
    bullets: [
      "Compare MZM, Ring, EAM, PCM, and DMD modulators under identical load conditions",
      "Sweep EOM extinction ratio and bit precision to find your accuracy floor",
      "Run the throughput model against your target TOPS before committing to array geometry",
    ],
  },
  {
    num: "02",
    name: "AI hardware companies",
    body: "You're building the chip that runs the model. The simulator tells you what the model costs in photonic hardware — and where the bottleneck will be before your first tapeout.",
    bullets: [
      "Identify ADC, DAC, or modulator rate as the binding throughput constraint",
      "Tune array width and tile count against real inference latency numbers",
      "Test CIFAR-10 CNNs and ResNet-8 through your proposed hardware configuration",
    ],
  },
  {
    num: "03",
    name: "Research labs",
    bullets: [
      "Sweep across five modulator technologies — MZM, micro-ring, EAM, PCM, and DLP — and compare inference accuracy under identical noise conditions",
      "Tune eight physical non-idealities independently: EOM bit precision, shot noise, laser RIN, source power, extinction ratio, insertion loss, thermal phase drift, and channel crosstalk",
      "Toggle between incoherent (direct detection) and coherent (homodyne) detection modes — with LO power, phase noise, and balanced CMRR controls for the coherent path",
      "Run parameterized sweeps with repeatable seeds so results are reproducible across configurations",
      "All physics grounded in peer-reviewed literature: Shen et al. (Nature Photonics, 2017), Feldmann et al. (Nature, 2021), Hamerly et al. (PRX, 2019)",
    ],
  },
  {
    num: "04",
    name: "ML systems engineers",
    bullets: [
      "Model real hardware throughput before any silicon exists — see MIPS, TOPS, and per-sample latency update live as you change array width and tile configuration",
      "Bottleneck detection tells you exactly where time is going: ADC readout, DAC programming, or weight loading — so you know what to optimize before tapeout",
      "Run the Pareto explorer to sweep the full accuracy-vs-cost design space and find your operating point across noise, precision, and array size simultaneously",
      "Compare photonic and ideal inference on the same model and the same batch — dual confusion matrices update in real time so the accuracy difference is immediately visible",
      "Supports multi-wavelength compute and waveguide tiling abstractions — a real hardware sizing tool",
    ],
  },
  {
    num: "05",
    name: "Model-optimization teams",
    body: "Quantization, pruning, and mixed-precision decisions all have a photonic cost. Run your compressed model through the simulator before assuming the hardware can absorb the trade-off.",
    bullets: [
      "Set EOM bit precision from 1–8 bits and watch accuracy respond in real time",
      "Compare photonic and ideal confusion matrices at each precision level",
      "Find the lowest precision your accuracy target can tolerate — then check the throughput gain",
    ],
  },
];

export default function AudienceSection() {
  return (
    <section id="audience" className="relative pb-28 md:pb-40" data-testid="audience-section">
      <div className="container-x grid grid-cols-1 gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Reveal>
            <div className="lg:sticky lg:top-28">
              <p className="eyebrow mb-4">Who it&apos;s for</p>
              <h2 className="text-4xl font-semibold tracking-tighter md:text-5xl">
                Built for the people who will build with light.
              </h2>
              <p className="mt-6 max-w-xs font-light leading-relaxed text-zinc-400">
                Five teams, one simulator — each arrives with a different question and leaves
                with the same answer.
              </p>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-8">
          {AUDIENCES.map((a, i) => (
            <Reveal key={a.num} delay={0.04 * i}>
              <div
                className="group flex items-baseline gap-6 border-t border-white/10 py-8 transition-colors last:border-b hover:bg-white/[0.02] md:gap-10 md:px-4"
                data-testid={`audience-${a.num}`}
              >
                <span className="font-mono text-xs font-bold text-beam">{a.num}</span>
                <div className="flex flex-1 flex-col gap-2 md:flex-row md:items-baseline md:justify-between md:gap-10">
                  <h3 className="text-xl font-medium tracking-tight transition-colors group-hover:text-beam md:text-2xl">
                    {a.name}
                  </h3>
                  <div>
                    {a.body && (
                      <p className="max-w-xl text-sm font-light leading-relaxed text-zinc-400">{a.body}</p>
                    )}
                    <ul className={`space-y-2.5 ${a.body ? "mt-3" : ""}`}>
                      {a.bullets.map((b) => (
                        <li key={b} className="flex items-baseline gap-3 font-mono text-xs leading-relaxed text-zinc-500">
                          <span className="text-zinc-700">·</span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
